/**
 * Start an enquiry that did not arrive by mail — a phone call, a WhatsApp message, a walk-in.
 *
 * ---- Why a dialog and not just a button ----
 *
 * The board's "✚ New enquiry" was disabled for a long time, and correctly: `create_enquiry`
 * refuses a document without BOTH `customer` and `delivery_location` (measured live:
 * `MandatoryError: [Enquiry, …]: customer, delivery_location`), so a bare button could only ever
 * produce a raw Frappe error. `CreateEnquiryBar` solves the same problem for mail by guessing both
 * from the message; with no message there is nothing to guess from, so it asks.
 *
 * Three fields, and no more. Everything else the RFQ builder can edit afterwards, and a form that
 * asks for what can be fixed later is a form people learn to skip.
 *
 * `company` is here for the reason `CreateEnquiryBar` explains at length: Bosun runs seven legal
 * entities, the enquiry is the root every RFQ and quotation inherits from, and nothing about a
 * phone call implies which one. It defaults to the session's company so the second enquiry of the
 * day costs no thought.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '../../components/Modal'
import { Button } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { useToast } from '../../components/Toast'
import { useCreateEnquiry } from '../../lib/api/useEnquiryMutations'
import { useDeliveryLocationPicker } from '../../lib/api/useEnquiryFromMail'
import { useCustomerSearch } from '../../lib/api/useCustomerRecord'
import { useCompanyPicker, useCurrentUser, useDeliveryLocationPriceLists } from '../../lib/api/useMasters'
import { setCompany, useSessionCompany } from '../../lib/api/company'
import { useEnquiryTypes } from '../../lib/api/useEnquiryTypes'
import type { EnquiryType } from '../../lib/types'


export function NewEnquiryModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const currentUser = useCurrentUser()
  // The SERVER's `Enquiry Type` master — every type it has, not the two this modal used to hard-code
  // (Akhil, 2026-09-19: "not seeing an option to set all types of enquiry").
  const enquiryTypes = useEnquiryTypes()

  const [customer, setCustomer] = useState('')
  const [location, setLocation] = useState('')
  const sessionCompany = useSessionCompany()
  const [companyChoice, setCompanyChoice] = useState('')
  // This dialog's own pick, else the session's (restored from the profile after a refresh).
  const company = companyChoice || sessionCompany || ''
  // Customers of the company picked below — `get_customers` needs it (Akhil, 5d).
  const useScopedCustomers = (search: string) => useCustomerSearch(search, company)
  const [type, setType] = useState<EnquiryType>('Technical')

  const priceLists = useDeliveryLocationPriceLists()
  const create = useCreateEnquiry({
    onSuccess: (enquiry, warning) => {
      // The warning wins the toast: "created" is the less useful half when something after the
      // create did not land, and the purchaser needs to know which follow-up to do by hand.
      toast(warning ?? `Enquiry ${enquiry.id} created`)
      // Straight to the builder: an enquiry with no lines is not finished, and this is where the
      // items get added.
      navigate(`/rfq/${enquiry.id}`)
    },
    onError: (error) => toast(error.message),
  })

  const ready = customer !== '' && location !== '' && company !== ''

  return (
    <Modal title="New enquiry" onClose={onClose}>
      <div className="pad" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
        For a requirement that arrived by phone or message rather than mail. You will add the items
        on the next screen.
      </div>

      <div className="pad" style={{ display: 'grid', gap: 10 }}>
        {/* Company first (Akhil, 2026-09-22): the customer search is scoped to it. */}
        <label className="ef">
          <span>Company</span>
          <LinkCombo
            value={company}
            onChange={(next) => {
              setCompanyChoice(next)
              // Remembered for the session, same as the mail path.
              setCompany(next)
            }}
            useOptions={useCompanyPicker}
            placeholder="Pick the operating company…"
          />
        </label>

        <label className="ef">
          <span>Enquiry type</span>
          <select value={type} onChange={(e) => setType(e.target.value as EnquiryType)} disabled={enquiryTypes.isLoading}>
            {(enquiryTypes.data ?? ['Technical', 'Provision']).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <label className="ef">
          <span>Delivery location</span>
          <LinkCombo
            value={location}
            onChange={setLocation}
            useOptions={useDeliveryLocationPicker}
            placeholder="Search locations…"
          />
        </label>

        <label className="ef">
          <span>Customer</span>
          <LinkCombo value={customer} onChange={setCustomer} useOptions={useScopedCustomers} placeholder="Search customers…" />
        </label>
      </div>

      <div className="pad" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          variant="primary"
          disabled={!ready || create.isPending}
          onClick={() =>
            create.mutate({
              source: 'manual',
              customer,
              company,
              delivery_location: location,
              // The location's selling price list, written with it (2026-09-17).
              price_list: location ? (priceLists.data?.get(location) ?? null) : null,
              enquiry_type: type,
              // Omitted when the signed-in user could not be resolved — `purchaser` goes through
              // its own `assign_purchaser` call, and an unresolvable name fails the whole create.
              purchaser: currentUser.data?.email ?? undefined,
              items: [],
            })
          }
        >
          {create.isPending ? 'Creating…' : 'Create & add items →'}
        </Button>
        {!ready ? (
          // Says which field is missing rather than leaving a dead button to puzzle over.
          <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>
            {customer === ''
              ? 'Pick a customer'
              : location === ''
                ? 'Pick a delivery location'
                : 'Pick the operating company'}{' '}
            — the server refuses an enquiry without it.
          </span>
        ) : null}
      </div>
    </Modal>
  )
}
